import React, { useEffect } from "react";
import { Link } from "react-router-dom";

import "./styles.css";

const COMPANY = "Infosecurity Soluções Digitais";
const CNPJ = "23.620.601/0001-00";
const EMAIL = "infosecluzmg@gmail.com";
const UPDATED_AT = "23 de outubro de 2026";

const MailLink = ({ subject }) => (
  <a href={`mailto:${EMAIL}?subject=${encodeURIComponent(subject)}`}>{EMAIL}</a>
);

const LegalLayout = ({ title, description, children }) => {
  useEffect(() => {
    document.title = `${title} | Infochat`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link className="legal-brand" to="/login" aria-label="Infochat - página inicial">
          <span className="legal-brand-mark">I</span>
          <span>Infochat</span>
        </Link>
        <Link className="legal-login-link" to="/login">Entrar</Link>
      </header>

      <main className="legal-shell">
        <article className="legal-card">
          <div className="legal-intro">
            <span className="legal-eyebrow">Infochat</span>
            <h1>{title}</h1>
            <p>{description}</p>
            <small>Última atualização: {UPDATED_AT}</small>
          </div>
          <div className="legal-content">{children}</div>
        </article>
      </main>

      <footer className="legal-footer">
        <nav aria-label="Documentos legais">
          <Link to="/privacy-policy">Política de Privacidade</Link>
          <Link to="/terms-of-service">Termos de Serviço</Link>
          <Link to="/data-deletion">Exclusão de Dados</Link>
        </nav>
        <p>{COMPANY} · CNPJ {CNPJ}</p>
      </footer>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h2>{title}</h2>
    {children}
  </section>
);

export const PrivacyPolicy = () => (
  <LegalLayout
    title="Política de Privacidade"
    description="Esta Política explica como tratamos dados pessoais na plataforma Infochat e em suas integrações."
  >
    <Section title="1. Quem somos">
      <p>
        O Infochat é fornecido por <strong>{COMPANY}</strong>, inscrita no CNPJ sob o nº {CNPJ},
        responsável pelo tratamento de dados nos limites descritos nesta Política. Para assuntos de
        privacidade, fale conosco pelo e-mail <MailLink subject="Privacidade e proteção de dados - Infochat" />.
      </p>
    </Section>

    <Section title="2. A quem esta Política se aplica">
      <p>
        Esta Política se aplica a usuários do Infochat, representantes de empresas clientes, visitantes de
        nossas páginas e pessoas que interagem com canais conectados por nossos clientes, como WhatsApp,
        Facebook e Instagram.
      </p>
      <p>
        Quando uma empresa cliente usa o Infochat para atender seus próprios contatos, ela normalmente decide
        as finalidades desse tratamento e atua como controladora. Nessa situação, a {COMPANY} atua como
        operadora, seguindo as instruções legítimas da empresa cliente e a legislação aplicável.
      </p>
    </Section>

    <Section title="3. Dados que podemos tratar">
      <ul>
        <li>dados de cadastro, como nome, e-mail, telefone, empresa e função;</li>
        <li>dados de autenticação, registros de acesso, endereço IP, dispositivo e eventos de segurança;</li>
        <li>identificadores e metadados de contas, Páginas e perfis conectados à Meta;</li>
        <li>credenciais técnicas e tokens de integração, protegidos e usados para manter as conexões autorizadas;</li>
        <li>mensagens, anexos, contatos, etiquetas e dados de atendimento processados em nome dos clientes;</li>
        <li>informações de suporte, diagnóstico, auditoria e uso das funcionalidades.</li>
      </ul>
    </Section>

    <Section title="4. Como usamos os dados">
      <p>Tratamos os dados para:</p>
      <ul>
        <li>criar contas, autenticar usuários e prestar as funcionalidades contratadas;</li>
        <li>conectar canais autorizados e encaminhar mensagens e eventos de atendimento;</li>
        <li>prestar suporte, prevenir fraudes, investigar falhas e proteger a plataforma;</li>
        <li>cumprir obrigações legais, regulatórias e atender autoridades competentes;</li>
        <li>medir e aperfeiçoar desempenho, estabilidade e experiência do serviço.</li>
      </ul>
      <p>
        Conforme o contexto, o tratamento pode se fundamentar na execução de contrato, no cumprimento de
        obrigação legal ou regulatória, no legítimo interesse, no exercício regular de direitos e no
        consentimento, quando ele for necessário.
      </p>
    </Section>

    <Section title="5. Integrações e compartilhamento">
      <p>
        Podemos compartilhar dados estritamente necessários com a Meta Platforms e outros provedores de canais,
        empresas de hospedagem, infraestrutura, segurança, suporte e demais operadores contratados. Também
        poderemos compartilhar informações para cumprir a lei, ordem judicial ou solicitação válida de autoridade.
        Não vendemos dados pessoais.
      </p>
      <p>
        Algumas empresas fornecedoras podem tratar dados fora do Brasil. Nesses casos, adotamos medidas
        compatíveis com a LGPD para proteger as informações e exigir tratamento adequado.
      </p>
    </Section>

    <Section title="6. Retenção e segurança">
      <p>
        Mantemos os dados pelo período necessário para prestar o serviço, cumprir obrigações legais, prevenir
        fraudes e exercer direitos. Depois disso, eles são eliminados ou anonimizados, salvo quando a lei permitir
        ou exigir sua conservação. Aplicamos controles técnicos e administrativos proporcionais aos riscos,
        embora nenhum ambiente digital seja totalmente livre de incidentes.
      </p>
    </Section>

    <Section title="7. Seus direitos">
      <p>
        Nos termos da LGPD, você pode solicitar confirmação e acesso, correção, anonimização, bloqueio ou
        eliminação, portabilidade quando aplicável, informações sobre compartilhamento, revisão de decisões
        automatizadas e revogação do consentimento. A solicitação pode exigir verificação de identidade e será
        atendida nos limites e prazos da legislação.
      </p>
      <p>
        Envie sua solicitação para <MailLink subject="Solicitação LGPD - Infochat" /> ou consulte nossas
        {" "}<Link to="/data-deletion">instruções de exclusão de dados</Link>.
      </p>
    </Section>

    <Section title="8. Crianças e adolescentes">
      <p>
        O Infochat é destinado a atividades profissionais e empresariais e não é direcionado a crianças. Caso
        identifiquemos tratamento inadequado de dados de crianças ou adolescentes, adotaremos as medidas cabíveis.
      </p>
    </Section>

    <Section title="9. Alterações desta Política">
      <p>
        Esta Política poderá ser atualizada para refletir mudanças legais, técnicas ou operacionais. A versão
        vigente e sua data de atualização permanecerão disponíveis nesta página.
      </p>
    </Section>
  </LegalLayout>
);

export const TermsOfService = () => (
  <LegalLayout
    title="Termos de Serviço"
    description="Estes Termos regulam o acesso e o uso da plataforma Infochat."
  >
    <Section title="1. Aceitação e identificação do fornecedor">
      <p>
        Ao contratar, acessar ou usar o Infochat, você declara que leu e concorda com estes Termos. O serviço é
        fornecido por <strong>{COMPANY}</strong>, CNPJ {CNPJ}. Se você usa a plataforma em nome de uma empresa,
        declara possuir poderes para vinculá-la a estes Termos.
      </p>
    </Section>

    <Section title="2. O serviço">
      <p>
        O Infochat reúne recursos de atendimento, organização de contatos, equipes, automações e integração com
        canais de terceiros. Funcionalidades, limites e condições comerciais podem variar conforme o plano
        contratado e propostas aceitas entre as partes.
      </p>
    </Section>

    <Section title="3. Conta e responsabilidades do usuário">
      <ul>
        <li>fornecer informações corretas e manter credenciais e acessos sob sua guarda;</li>
        <li>autorizar apenas pessoas habilitadas e comunicar suspeitas de uso indevido;</li>
        <li>obter bases legais e autorizações necessárias para tratar dados e enviar comunicações;</li>
        <li>respeitar a LGPD, o Marco Civil da Internet e demais normas aplicáveis;</li>
        <li>cumprir os termos e políticas da Meta, WhatsApp, Facebook, Instagram e outros canais integrados.</li>
      </ul>
    </Section>

    <Section title="4. Uso permitido">
      <p>
        É proibido usar o serviço para atividades ilícitas, fraude, assédio, spam, violação de direitos de
        terceiros, distribuição de código malicioso, tentativa de acesso não autorizado ou interferência na
        segurança e disponibilidade da plataforma. Também é proibido contornar limites técnicos ou utilizar as
        integrações em desacordo com as regras dos respectivos provedores.
      </p>
    </Section>

    <Section title="5. Integrações de terceiros">
      <p>
        Integrações dependem de serviços, permissões, análises e políticas de terceiros. Alterações, bloqueios ou
        indisponibilidades nesses serviços podem afetar funcionalidades do Infochat. O cliente é responsável por
        manter suas contas conectadas, permissões válidas e conformidade com as plataformas utilizadas.
      </p>
    </Section>

    <Section title="6. Privacidade e dados">
      <p>
        O tratamento de dados pessoais observa nossa <Link to="/privacy-policy">Política de Privacidade</Link>.
        O cliente é responsável pelas instruções, bases legais e comunicações relacionadas aos dados que inserir
        ou tratar por meio do Infochat.
      </p>
    </Section>

    <Section title="7. Propriedade intelectual">
      <p>
        A plataforma, suas marcas, interfaces, códigos e conteúdos próprios pertencem à {COMPANY} ou a seus
        licenciadores. O cliente recebe uma licença limitada, revogável, não exclusiva e intransferível para usar
        o serviço durante a vigência da contratação. O cliente preserva os direitos sobre seus próprios dados e
        conteúdos.
      </p>
    </Section>

    <Section title="8. Disponibilidade e suporte">
      <p>
        Empregamos esforços razoáveis para manter o serviço seguro e disponível, mas manutenções, falhas de rede,
        atos de terceiros e eventos fora de controle podem causar interrupções. Canais e níveis de suporte seguem
        o plano ou instrumento comercial aplicável.
      </p>
    </Section>

    <Section title="9. Suspensão e encerramento">
      <p>
        O acesso poderá ser suspenso ou encerrado em caso de inadimplência, risco de segurança, violação destes
        Termos, uso ilegal ou exigência de autoridade ou provedor integrado. O usuário pode solicitar o
        encerramento e a exclusão de dados conforme as <Link to="/data-deletion">instruções desta página</Link>,
        respeitadas as obrigações legais de retenção.
      </p>
    </Section>

    <Section title="10. Responsabilidade">
      <p>
        Cada parte responde pelos danos diretos que comprovadamente causar, nos limites permitidos pela legislação
        aplicável e pelas condições comerciais contratadas. Nada nestes Termos exclui responsabilidade que não
        possa ser limitada por lei, inclusive direitos assegurados ao consumidor quando aplicáveis.
      </p>
    </Section>

    <Section title="11. Alterações, lei e contato">
      <p>
        Podemos atualizar estes Termos, divulgando a versão vigente e sua data nesta página. Aplicam-se as leis da
        República Federativa do Brasil e as regras legais de competência, inclusive o foro do domicílio do
        consumidor quando aplicável. Dúvidas podem ser enviadas para
        {" "}<MailLink subject="Termos de Serviço - Infochat" />.
      </p>
    </Section>
  </LegalLayout>
);

export const DataDeletion = () => (
  <LegalLayout
    title="Instruções para Exclusão de Dados"
    description="Você pode solicitar a exclusão dos dados associados ao Infochat e revogar o acesso às integrações da Meta."
  >
    <Section title="1. Envie sua solicitação">
      <p>
        Envie um e-mail para <MailLink subject="Solicitação de exclusão de dados - Infochat" /> com o assunto
        <strong> “Solicitação de exclusão de dados – Infochat”</strong> e informe:
      </p>
      <ul>
        <li>nome completo e e-mail usado no Infochat;</li>
        <li>nome da empresa ou organização vinculada à conta;</li>
        <li>perfil, Página, conta do Instagram ou outro identificador relacionado, quando aplicável;</li>
        <li>descrição objetiva dos dados ou da conta que deseja excluir.</li>
      </ul>
      <div className="legal-callout">
        <strong>Importante:</strong> para proteger sua conta, poderemos solicitar informações adicionais para
        confirmar sua identidade e sua autorização para representar a empresa indicada.
      </div>
    </Section>

    <Section title="2. Revogue a integração com a Meta">
      <p>
        Para interromper novos acessos, entre no Facebook com a conta que autorizou o Infochat, acesse
        <strong> Configurações e privacidade → Configurações → Integrações comerciais</strong>, selecione
        <strong> Infochat</strong> e escolha <strong>Remover</strong>. Os nomes dos menus podem variar conforme a
        versão do Facebook.
      </p>
      <p>
        A remoção da integração revoga permissões e impede novos acessos, mas não substitui o pedido de exclusão
        de dados já armazenados. Para isso, envie também o e-mail descrito acima.
      </p>
    </Section>

    <Section title="3. O que acontece depois">
      <p>
        Confirmaremos o recebimento, verificaremos a identidade do solicitante e informaremos o andamento. Quando
        o pedido for válido, eliminaremos ou anonimizaremos os dados sob responsabilidade da {COMPANY}, incluindo
        credenciais de integração e metadados vinculados, conforme aplicável.
      </p>
      <p>
        Dados de contatos e atendimentos tratados em nome de uma empresa cliente podem depender de instrução dessa
        empresa, que atua como controladora. Nessa hipótese, encaminharemos ou orientaremos a solicitação ao
        responsável adequado.
      </p>
    </Section>

    <Section title="4. Retenções permitidas por lei">
      <p>
        Alguns registros podem ser conservados pelo tempo necessário ao cumprimento de obrigação legal ou
        regulatória, ao exercício regular de direitos, à prevenção de fraude ou a outras hipóteses autorizadas pela
        LGPD. Quando possível, os dados serão bloqueados para outras finalidades durante esse período.
      </p>
    </Section>

    <Section title="5. Responsável pelo atendimento">
      <p>
        <strong>{COMPANY}</strong><br />
        CNPJ: {CNPJ}<br />
        E-mail: <MailLink subject="Solicitação de exclusão de dados - Infochat" />
      </p>
    </Section>
  </LegalLayout>
);
